import React, { Component } from 'react'
import PropTypes from 'prop-types'
import update from 'immutability-helper'

import { DragDropContext, Droppable } from 'react-beautiful-dnd'

import { reorder, hiddenInPaging } from '../../../functions'

import TableColumn from './table-column'
import TableGroup from './table-group'
import TableRow from './table-row'

const previousPaging = (paging) => {
  return paging.offset > 0
}

const nextPaging = (paging, columns) => {
  return columns.length > (paging.offset+paging.size)
}

class Table extends Component {
  constructor(props) {
    super(props)

    this.state = {
      columns: [],
      items: [],
      paging: {
        stickyColumn: true,
        hideFirstColumn: true,
        offset: 0,
        size: 4
      },
      showAddColumnButton: true,
      focusCell: undefined
    }

    this.component = React.createRef()
    this.addButton = React.createRef()
  }

  componentWillMount() {
    const { columns, items } = this.props

    this.setState({columns, items})
  }

  componentDidMount() {
    this.calculateTableSize()
  }

  calculateTableSize() {
    const { offsetWidth } = this.component.current
    const channelWidth = 95
    const maxWidth = 180

    const size = Math.floor((offsetWidth - channelWidth) / maxWidth)
    this.setState({
      paging: update(this.state.paging, {
        size: {$set: size}
      })
    })
  }

  componentWillReceiveProps(nextProps) {
    const { columns, items } = nextProps

    if (columns !== this.props.columns) this.setState({columns})
    if (items !== this.props.items) this.setState({items})
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { fullView, columns, items, stageElements } = nextProps

    if (fullView !== this.props.fullView) return true
    if (columns !== this.props.columns) return true
    if (items !== this.props.items) return true
    if (stageElements !== this.props.stageElements) return true
    if (nextState !== this.state) return true
    return false
  }

  componentDidUpdate(prevProps, prevState) {
    const { fullView } = prevProps

    if (fullView !== this.props.fullView) this.calculateTableSize()
  }

  changeFocus(e, current) {
    e.preventDefault()

    const getElement = (current) => {
      const nextId = `ch${current.channel}-eq${current.cell}`
      const nextElement = document.getElementById(nextId)

      if (nextElement) {
        this.setState(undefined, () => nextElement.click())
        return nextElement
      }
    }

    if ((e.shiftKey && e.key === 'Enter') || e.key === 'ArrowUp') {
      current = {...current, channel: current.channel-1}
      const nextElement = getElement(current)

      if (!nextElement) getElement({...current, channel: current.channel+1})
    } else
    if (e.key === 'Enter') {
      current = {...current, channel: current.channel+1}
      const nextElement = getElement(current)

      if (!nextElement) {
        const { items } = this.props

        this.props.addRiderItem({
          class: "instrument",
          position: items.length+1,
          indexes: [
            items.length
          ]
        })
      }
    } else
    if ((e.shiftKey && e.key === 'Tab') || e.key === 'ArrowLeft') {
      current = {...current, cell: current.cell-1}
      const nextElement = getElement(current)

      if (!nextElement) {
        const { columns, paging } = this.state

        if (previousPaging(paging)) {
          this.paginate(paging, 'previous')
          this.setState(undefined, () => getElement(current))
        } else {
          getElement({...current, cell: current.cell+1})
        }
      }
    } else
    if (e.key === 'Tab') {
      current = {...current, cell: current.cell+1}
      const nextElement = getElement(current)

      if (!nextElement) {
        const { columns, paging } = this.state

        if (nextPaging(paging, columns)) {
          this.paginate(paging, 'next')
          this.setState(undefined, () => {
            const nextElement = getElement(current)

            if (!nextElement) {
              this.paginate(paging, 0)
              this.addButton.current.focus()
            }
          })
        } else {
          this.paginate(paging, 0)
          this.setState(undefined, () => {
            const nextElement = getElement({channel: current.channel+1, cell: 1})

            if (!nextElement) {
              const { items } = this.props

              this.props.addRiderItem({
                class: "instrument",
                position: items.length+1,
                indexes: [
                  items.length
                ]
              })
            }
          })
        }
      }
    } else
    if (e.key === 'ArrowRight') {
      current = {...current, cell: current.cell+1}
      const nextElement = getElement(current)

      if (!nextElement) {
        const { columns, paging } = this.state

        if (nextPaging(paging, columns)) {
          this.paginate(paging, 'next')
          this.setState(undefined, () => getElement(current))
        } else {
          getElement({...current, cell: current.cell-1})
        }
      }
    } else
    if (e.key === 'ArrowDown') {
      current = {...current, channel: current.channel+1}
      const nextElement = getElement(current)

      if (!nextElement) this.setState({focusCell: current.cell}, () => this.addButton.current.focus())
    }
  }

  reorderList = (result) => {
    if (!result.destination) return null

    if (result.type === 'column') {
      const { stickyColumn, offset } = this.state.paging

      let sourceIndex = result.source.index
      let destinationIndex = result.destination.index

      if (offset) {
        sourceIndex += offset
        destinationIndex += offset
      }

      const columns = reorder(
        this.state.columns,
        stickyColumn ? sourceIndex+1 : sourceIndex,
        stickyColumn ? destinationIndex+1 : destinationIndex
      )

      this.setState({columns}, () => this.props.moveRiderColumn({
        id: result.draggableId,
        position: stickyColumn ? destinationIndex+1 : destinationIndex
      }))
    } else
    if (result.type === 'instrument') {
      const items = reorder(
        this.state.items,
        result.source.index,
        result.destination.index
      )

      this.setState({items}, () => this.props.moveRiderItem({
        id: result.draggableId,
        position: result.destination.index+1
      }))
    } else
    if (result.type.includes('instrument-group')) {
      const findIndex = result.type.split('-')
      const index = findIndex[findIndex.length-1]

      const children = reorder(
        this.state.items[index].children,
        result.source.index,
        result.destination.index
      )

      this.setState({
        items: update(this.state.items, {
          [index]: {
            children: {$set: children}
          }
        })
      }, () => this.props.moveRiderItem({
        id: result.draggableId,
        position: result.destination.index+1
      }))
    }
  }

  paginate(paging, operator) {
    this.setState({
      paging: update(paging, {
        offset: {
          $set: !isNaN(operator)
          ? operator
          : operator === 'next'
            ? paging.offset+1
            : paging.offset-1
        }
      })
    })
  }

  render() {
    const { view, stageElements } = this.props
    const { columns, items, paging, showAddColumnButton } = this.state

    if (!items) return null

    return (
      <div ref={this.component} className="table">
        <DragDropContext onDragEnd={this.reorderList}>
          <div className="table-head">
            <div className="table-column table-column--channel">
              Channel
            </div>

            <Droppable droppableId="table-head" isDropDisabled={!view.editable} direction="horizontal" type="column">
              {(provided, snapshot) => (
                <div className="table-head--droppable"
                  ref={provided.innerRef} >
                  {
                    paging.stickyColumn
                    ? <div className="table-column table-column--instrument" style={{minWidth: `${100/paging.size}%`, maxWidth: `${100/paging.size}%`}}>
                        Instrument
                      </div>
                    : null
                  }

                  {
                    columns.map((column, index) => {
                      if (paging.stickyColumn) {
                        if (column.type === 'instrument' || hiddenInPaging(paging, index)) return null
                      } else {
                        if (hiddenInPaging(paging, index)) return null
                      }

                      return (
                        <TableColumn key={index}
                          view={view}
                          column={column}
                          paging={paging}
                          availableColumnTypes={stageElements.availableColumnTypes}
                          removeRiderColumn={() => {
                            this.setState({
                              showAddColumnButton: true
                            }, () => {
                              this.paginate(paging, 'previous')
                              this.props.removeRiderColumn(index)
                            })
                          }}
                          createRiderColumn={(column) => {
                            this.setState({
                              showAddColumnButton: true
                            }, () => {
                              this.props.createRiderColumn(column)
                            })
                          }}
                          deleteRiderColumn={(columnId) => {
                            this.paginate(paging, 'previous')
                            this.props.deleteRiderColumn(columnId)
                          }} />
                      )
                    })
                  }
                </div>
              )}
            </Droppable>

            {
              showAddColumnButton && view.editable
              ? <div className="table--add-column-button" onClick={() => {
                  this.setState({
                    showAddColumnButton: false
                  }, () => {
                    this.paginate(paging, columns.length-paging.size+1)
                    this.props.addRiderColumn({
                      position: columns.length
                    })
                  })
                }}>
                  <div className="oval-button oval-button--add" />
                </div>
              : null
            }
          </div>

          <div className="table-body">
            {
              previousPaging(paging)
              ? <div className="table--fixed-wrapper">
                  <div className="table--previous-button" style={{left: `${100/paging.size}%`}} onClick={() => this.paginate(paging, 'previous')}>
                    <div className="oval-button oval-button--previous" />
                  </div>
                </div>
              : null
            }

            {
              nextPaging(paging, columns)
              ? <div className="table--next-button" onClick={() => this.paginate(paging, 'next')}>
                  <div className="oval-button oval-button--next" />
                </div>
              : null
            }

            <Droppable droppableId="table-body" isDropDisabled={!view.editable} type="instrument">
              {(provided, snapshot) => (
                <div className="table-body--droppable"
                  ref={provided.innerRef} >
                  {
                    items.length > 0
                    ? items.map((item, index) => {
                        // if (item.id === undefined) return <Loading key={index} className="table-row table-row--loading" />

                        if (item.class === 'instrument-group') {
                          return (
                            <TableGroup key={item.id || index}
                              view={view}
                              dragDropType={`instrument-group-${index}`}
                              group={this.props.group}
                              columns={columns}
                              item={item}
                              rowIndex={index}
                              paging={paging}
                              itemGroups={this.props.itemGroups}
                              changeFocus={(e, current) => this.changeFocus(e, current)}
                              addRiderItem={this.props.addRiderItem}
                              removeRiderItem={this.props.removeRiderItem}
                              createRiderItem={this.props.createRiderItem}
                              createRiderItems={this.props.createRiderItems}
                              editRiderItem={this.props.editRiderItem}
                              deleteRiderItem={this.props.deleteRiderItem}
                              stageElements={stageElements}
                              createStageElement={this.props.createStageElement}
                              getSearchResults={this.props.getSearchResults} />
                          )
                        } else
                        if (item.class === 'instrument') {
                          return (
                            <TableRow key={item.id || index}
                              view={view}
                              dragDropType="instrument"
                              group={this.props.group}
                              columns={columns}
                              item={item}
                              rowIndex={index}
                              paging={paging}
                              itemGroups={this.props.itemGroups}
                              changeFocus={(e, current) => this.changeFocus(e, current)}
                              addRiderItem={this.props.addRiderItem}
                              removeRiderItem={this.props.removeRiderItem}
                              createRiderItem={this.props.createRiderItem}
                              editRiderItem={this.props.editRiderItem}
                              deleteRiderItem={this.props.deleteRiderItem}
                              stageElements={stageElements}
                              createStageElement={this.props.createStageElement}
                              getSearchResults={this.props.getSearchResults} />
                          )
                        } else {
                          return null
                        }
                      })
                    : view.editable
                      ? <p>Start by adding your <a onClick={() => {
                          this.props.addRiderItem({
                            class: "instrument",
                            position: items.length+1,
                            indexes: [
                              items.length
                            ]
                          })
                        }}>first instrument</a>.</p>
                      : <p>This rider has no instruments</p>
                  }

                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>

        {
          view.editable
          ? <button ref={this.addButton} className="table--add-button button--link" onClick={() => {
              this.props.addRiderItem({
                class: "instrument",
                position: items.length+1,
                indexes: [
                  items.length
                ]
              })
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp') {
                const { focusCell } = this.state

                const item = items[items.length-1]
                let channel = item.channel

                if (isNaN(channel)) {
                  const child = item.children[item.children.length-1]
                  channel = child.channel
                }

                this.changeFocus(e, {channel: channel+1, cell: focusCell || 1})
              }
            }}
            tabIndex="1">
              Insert instrument
            </button>
          : null
        }
      </div>
    )
  }
}

Table.propTypes = {
  view: PropTypes.object,
  fullView: PropTypes.bool,
  group: PropTypes.string,
  columns: PropTypes.array,
  addRiderColumn: PropTypes.func,
  removeRiderColumn: PropTypes.func,
  createRiderColumn: PropTypes.func,
  moveRiderColumn: PropTypes.func,
  deleteRiderColumn: PropTypes.func,
  items: PropTypes.array,
  itemGroups: PropTypes.array,
  addRiderItem: PropTypes.func,
  removeRiderItem: PropTypes.func,
  createRiderItem: PropTypes.func,
  createRiderItems: PropTypes.func,
  editRiderItem: PropTypes.func,
  moveRiderItem: PropTypes.func,
  deleteRiderItem: PropTypes.func,
  stageElements: PropTypes.object,
  createStageElement: PropTypes.func,
  getSearchResults: PropTypes.func
}

Table.defaultProps = {
  view: {},
  fullView: false,
  group: '',
  columns: [],
  addRiderColumn: function() {},
  removeRiderColumn: function() {},
  createRiderColumn: function() {},
  moveRiderColumn: function() {},
  deleteRiderColumn: function() {},
  items: [],
  itemGroups: [],
  addRiderItem: function() {},
  removeRiderItem: function() {},
  createRiderItem: function() {},
  createRiderItems: function() {},
  editRiderItem: function() {},
  moveRiderItem: function() {},
  deleteRiderItem: function() {},
  stageElements: {},
  createStageElement: function() {},
  getSearchResults: function() {}
}

export default Table



// WEBPACK FOOTER //
// ./src/components/rider/rider-input/input-table/table.js